import React from 'react';
import { UseFormReturn, FieldArrayWithId, UseFieldArrayAppend, UseFieldArrayRemove } from 'react-hook-form';
import AddressInput, { AddressData } from '../ui/AddressInput';
import { PatientFormData } from './patientForm.types';
import { State } from '@/models/state.model';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Package, CreditCard } from 'lucide-react';

type AddressField = FieldArrayWithId<PatientFormData, 'addresses', 'id'>;

interface AddressSectionProps {
  form: UseFormReturn<PatientFormData>;
  addressFields: AddressField[];
  states: State[];
  handleAddressChange: (index: number) => (addressData: AddressData) => void;
  appendAddress: UseFieldArrayAppend<PatientFormData, 'addresses'>;
  removeAddress: UseFieldArrayRemove;
  hideRequiredMarkers?: boolean;
}

const AddressSection = ({
  form,
  addressFields,
  states,
  handleAddressChange,
  appendAddress,
  removeAddress,
  hideRequiredMarkers = false,
}: AddressSectionProps) => {
  const shippingEnabled = form.watch('shippingAddressEnabled');

  // Find the billing and shipping address indices
  const billingIndex = addressFields.findIndex(
    (_, i) => form.watch(`addresses.${i}.address_type`) === 'billing'
  );
  const shippingIndex = addressFields.findIndex(
    (_, i) => form.watch(`addresses.${i}.address_type`) === 'shipping'
  );

  // Use first address as billing if none explicitly marked
  const effectiveBillingIndex = billingIndex >= 0 ? billingIndex : 0;

  const handleShippingToggle = (checked: boolean) => {
    form.setValue('shippingAddressEnabled', checked);
    form.setValue('shippingSameAsBilling', !checked);

    if (checked && shippingIndex < 0) {
      // Add a new shipping address entry
      appendAddress({
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        postalCode: '',
        address_type: 'shipping',
      });
    } else if (!checked && shippingIndex >= 0) {
      // Remove the shipping address entry
      removeAddress(shippingIndex);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-[0.8rem] font-semibold uppercase text-[#60378B]">Address</h3>

      {/* Billing Address */}
      {effectiveBillingIndex >= 0 && addressFields[effectiveBillingIndex] && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <CreditCard className="h-4 w-4 text-slate-500" />
            <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">
              Billing Address
            </span>
            {!hideRequiredMarkers && <span className="text-red-500">*</span>}
          </div>
          <AddressInput
            value={{
              addressLine1: form.watch(`addresses.${effectiveBillingIndex}.addressLine1`) || '',
              addressLine2: form.watch(`addresses.${effectiveBillingIndex}.addressLine2`) || '',
              city: form.watch(`addresses.${effectiveBillingIndex}.city`) || '',
              state: form.watch(`addresses.${effectiveBillingIndex}.state`) || '',
              postalCode: form.watch(`addresses.${effectiveBillingIndex}.postalCode`) || '',
            }}
            onChange={handleAddressChange(effectiveBillingIndex)}
            errors={{
              addressLine1: form.formState.errors.addresses?.[effectiveBillingIndex]?.addressLine1,
              addressLine2: form.formState.errors.addresses?.[effectiveBillingIndex]?.addressLine2,
              city: form.formState.errors.addresses?.[effectiveBillingIndex]?.city,
              state: form.formState.errors.addresses?.[effectiveBillingIndex]?.state,
              postalCode: form.formState.errors.addresses?.[effectiveBillingIndex]?.postalCode,
            }}
            states={states}
            hideRequiredMarkers={hideRequiredMarkers}
          />
        </div>
      )}

      {/* Shipping Address Toggle */}
      <div className="flex items-center gap-3 pt-2 pb-1">
        <Switch
          id="shipping-toggle"
          checked={shippingEnabled || false}
          onCheckedChange={handleShippingToggle}
        />
        <Label
          htmlFor="shipping-toggle"
          className="text-sm text-slate-700 cursor-pointer"
        >
          Add a different shipping address
        </Label>
      </div>

      {/* Shipping Address */}
      {shippingEnabled && (() => {
        // Re-find shipping index after potential append
        const currentShippingIndex = addressFields.findIndex(
          (_, i) => form.watch(`addresses.${i}.address_type`) === 'shipping'
        );
        if (currentShippingIndex < 0) return null;

        return (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Package className="h-4 w-4 text-slate-500" />
              <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                Shipping Address
              </span>
            </div>
            <AddressInput
              value={{
                addressLine1: form.watch(`addresses.${currentShippingIndex}.addressLine1`) || '',
                addressLine2: form.watch(`addresses.${currentShippingIndex}.addressLine2`) || '',
                city: form.watch(`addresses.${currentShippingIndex}.city`) || '',
                state: form.watch(`addresses.${currentShippingIndex}.state`) || '',
                postalCode: form.watch(`addresses.${currentShippingIndex}.postalCode`) || '',
              }}
              onChange={handleAddressChange(currentShippingIndex)}
              errors={{
                addressLine1: form.formState.errors.addresses?.[currentShippingIndex]?.addressLine1,
                addressLine2: form.formState.errors.addresses?.[currentShippingIndex]?.addressLine2,
                city: form.formState.errors.addresses?.[currentShippingIndex]?.city,
                state: form.formState.errors.addresses?.[currentShippingIndex]?.state,
                postalCode: form.formState.errors.addresses?.[currentShippingIndex]?.postalCode,
              }}
              states={states}
              hideRequiredMarkers={hideRequiredMarkers}
            />
          </div>
        );
      })()}

      {!shippingEnabled && (
        <p className="text-xs text-slate-400 italic">
          Shipping address will default to the billing address above.
        </p>
      )}
    </div>
  );
};

export default AddressSection;
